package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.domain.util.AppClock
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.ChecklistItem
import com.evoapps.evoshift.domain.model.InvalidChecklistException
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.ShiftChecklist
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.domain.usecase.checklist.ChecklistProgress
import com.evoapps.evoshift.domain.usecase.checklist.ObserveDayChecklistsUseCase
import com.evoapps.evoshift.domain.usecase.checklist.ObserveShiftChecklistsUseCase
import com.evoapps.evoshift.domain.usecase.checklist.ShiftChecklists
import com.evoapps.evoshift.domain.usecase.checklist.TickChecklistItemUseCase
import com.evoapps.evoshift.domain.usecase.checklist.tick
import com.evoapps.evoshift.session.WorkplaceSessionManager
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.checklist_note_required
import evoshift.composeapp.generated.resources.error_save_failed
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn
import kotlin.time.Instant

/** The note dialog: which item of which checklist, and what is typed so far. */
data class ChecklistNoteDraft(val checklistId: String, val item: ChecklistItem, val note: String)

data class ShiftChecklistUiState(
    val isLoading: Boolean = true,
    val shift: Shift? = null,
    val checklists: List<ShiftChecklist> = emptyList(),
    /** The shift is the reader's and they may change things (TICK_CHECKLISTS). */
    val canTick: Boolean = false,
    val noteDraft: ChecklistNoteDraft? = null,
    val error: UiText? = null
)

/**
 * SH-121: the checklists of one of the person's shifts. A tick shows at once and is saved behind
 * it; a failed save takes it back. An item that needs a note asks for it first.
 */
@OptIn(ExperimentalCoroutinesApi::class)
class ShiftChecklistViewModel(
    private val shiftId: String,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val shiftRepository: ShiftRepository,
    private val observeShiftChecklists: ObserveShiftChecklistsUseCase,
    private val tickItem: TickChecklistItemUseCase,
    private val plans: ObservePlanActiveUseCase? = null,
    private val now: () -> Instant = { AppClock.now() }
) : ViewModel() {

    private val _uiState = MutableStateFlow(ShiftChecklistUiState())
    val uiState: StateFlow<ShiftChecklistUiState> = _uiState.asStateFlow()

    init {
        viewModelScope.launch {
            val shift = shiftRepository.getShift(shiftId).getOrElse { e ->
                _uiState.update { it.copy(isLoading = false, error = e.toUiText()) }
                return@launch
            }
            val workplaceId = RosterIds.workplaceIdOf(shift.rosterId)
            getCurrentUserUseCase().map { it?.uid }.distinctUntilChanged()
                .flatMapLatest { uid ->
                    if (uid == null) flowOf(null)
                    else combine(observeShiftChecklists(shift, uid), capabilitiesAt(getCurrentUserUseCase(), plans, workplaceId)) { list, caps ->
                        Triple(uid, list, caps)
                    }
                }
                .catch { e -> _uiState.update { it.copy(isLoading = false, error = e.toUiText()) } } // SH-188
                .collect { loaded ->
                    _uiState.update { state ->
                        if (loaded == null) state.copy(isLoading = false, shift = shift)
                        else state.copy(
                            isLoading = false,
                            shift = shift,
                            checklists = loaded.second,
                            canTick = shift.userId == loaded.first && loaded.third.shows(Gate.TICK_CHECKLISTS)
                        )
                    }
                }
        }
    }

    /** Ticks or unticks [item]; an item that needs a note opens the note dialog instead. */
    fun toggle(checklist: ShiftChecklist, item: ChecklistItem) {
        if (!_uiState.value.canTick) return
        val done = !checklist.isDone(item.id)
        if (done && item.requiresNote) {
            _uiState.update { it.copy(noteDraft = ChecklistNoteDraft(checklist.id, item, checklist.noteOf(item.id).orEmpty())) }
            return
        }
        save(checklist, item, done, checklist.noteOf(item.id))
    }

    /** Opens the note dialog for [item] (to add or change a note on any item). */
    fun editNote(checklist: ShiftChecklist, item: ChecklistItem) {
        if (!_uiState.value.canTick) return
        _uiState.update { it.copy(noteDraft = ChecklistNoteDraft(checklist.id, item, checklist.noteOf(item.id).orEmpty())) }
    }

    fun updateNote(text: String) = _uiState.update { state -> state.copy(noteDraft = state.noteDraft?.copy(note = text)) }

    fun dismissNote() = _uiState.update { it.copy(noteDraft = null) }

    /** Saves the note and ticks the item (a note is what was done). */
    fun saveNote() {
        val draft = _uiState.value.noteDraft ?: return
        val checklist = _uiState.value.checklists.firstOrNull { it.id == draft.checklistId } ?: return
        if (draft.item.requiresNote && draft.note.isBlank()) {
            _uiState.update { it.copy(error = UiText.Resource(Res.string.checklist_note_required)) }
            return
        }
        _uiState.update { it.copy(noteDraft = null) }
        save(checklist, draft.item, done = true, note = draft.note)
    }

    private fun save(checklist: ShiftChecklist, item: ChecklistItem, done: Boolean, note: String?) {
        val ticked = try {
            checklist.tick(item.id, done, note, now())
        } catch (e: InvalidChecklistException) {
            _uiState.update { it.copy(error = UiText.Resource(Res.string.checklist_note_required)) }
            return
        }
        val before = _uiState.value.checklists
        _uiState.update { state -> state.copy(checklists = state.checklists.map { if (it.id == ticked.id) ticked else it }) }
        viewModelScope.launch {
            tickItem(checklist, item.id, done, note).onFailure { e ->
                _uiState.update { it.copy(checklists = before, error = UiText.Resource(Res.string.error_save_failed, e.toUiText())) }
            }
        }
    }

    fun messageShown() = _uiState.update { it.copy(error = null) }
}

data class MyChecklistsUiState(
    /** Today's shifts of the person at the current workplace that have checklists. */
    val shifts: List<ShiftChecklists> = emptyList(),
    /** SH-188: a failed listener's message. */
    val error: UiText? = null
)

/** SH-121: the dashboards' "Checklists today" card: the person's checklists for today's shifts. */
@OptIn(ExperimentalCoroutinesApi::class)
class MyChecklistsViewModel(
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val sessionManager: WorkplaceSessionManager,
    private val shiftRepository: ShiftRepository,
    private val observeShiftChecklists: ObserveShiftChecklistsUseCase,
    private val today: () -> LocalDate = { AppClock.todayIn(TimeZone.currentSystemDefault()) }
) : ViewModel() {

    private val _uiState = MutableStateFlow(MyChecklistsUiState())
    val uiState: StateFlow<MyChecklistsUiState> = _uiState.asStateFlow()

    init {
        viewModelScope.launch {
            combine(getCurrentUserUseCase().map { it?.uid }.distinctUntilChanged(), sessionManager.currentWorkplaceId) { uid, wp -> uid to wp }
                .distinctUntilChanged()
                .flatMapLatest { (uid, wp) ->
                    val date = today().toString()
                    if (uid == null || wp == null) flowOf(emptyList())
                    else shiftRepository.observeShiftsForUser(uid, date)
                        .map { shifts ->
                            shifts.filter { it.date == date && it.userId == uid && RosterIds.workplaceIdOf(it.rosterId) == wp }
                                .sortedBy { it.plannedTime.start }
                        }
                        .distinctUntilChanged { a, b -> a.map { it.id to it.plannedTime } == b.map { it.id to it.plannedTime } }
                        .flatMapLatest { shifts ->
                            if (shifts.isEmpty()) flowOf(emptyList())
                            else combine(shifts.map { shift -> observeShiftChecklists(shift, uid).map { ShiftChecklists(shift, it) } }) { rows ->
                                rows.filter { it.checklists.isNotEmpty() }
                            }
                        }
                }
                .catch { e -> _uiState.update { it.copy(error = e.toUiText()) } } // SH-188
                .collect { rows -> _uiState.update { it.copy(shifts = rows) } }
        }
    }
}

data class TodayChecklistsUiState(
    /** Today's checklists per shift id, for "Today at work". */
    val byShift: Map<String, ChecklistProgress> = emptyMap(),
    val workplaceId: String? = null,
    val date: String? = null,
    /** SH-188: a failed listener's message. */
    val error: UiText? = null
)

/**
 * SH-121: the checklist progress on "Today at work" (owner and manager dashboards): for each of
 * today's shifts, how many of its checklists are done. Only for those who see the card.
 */
@OptIn(ExperimentalCoroutinesApi::class)
class TodayChecklistsViewModel(
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val sessionManager: WorkplaceSessionManager,
    private val observeDay: ObserveDayChecklistsUseCase,
    private val plans: ObservePlanActiveUseCase? = null,
    private val today: () -> LocalDate = { AppClock.todayIn(TimeZone.currentSystemDefault()) }
) : ViewModel() {

    private val _uiState = MutableStateFlow(TodayChecklistsUiState())
    val uiState: StateFlow<TodayChecklistsUiState> = _uiState.asStateFlow()

    init {
        viewModelScope.launch {
            sessionManager.currentWorkplaceId
                .flatMapLatest { wp ->
                    if (wp == null) flowOf(TodayChecklistsUiState())
                    else capabilitiesAt(getCurrentUserUseCase(), plans, wp)
                        .map { Capability.EDIT_ROSTER in it && it.shows(Gate.CHECKLISTS) }
                        .distinctUntilChanged()
                        .flatMapLatest { allowed ->
                            val date = today().toString()
                            if (!allowed) flowOf(TodayChecklistsUiState(workplaceId = wp, date = date))
                            else observeDay(wp, date).map { rows ->
                                TodayChecklistsUiState(rows.associate { it.shift.id to ChecklistProgress.of(it.checklists) }, wp, date)
                            }
                        }
                }
                .catch { e -> _uiState.update { it.copy(error = e.toUiText()) } } // SH-188
                .collect { state -> _uiState.value = state }
        }
    }
}
